import React, { Fragment, useState } from 'react';
import ChatGPTPresetDrawer from '../ChatGPTPresetDrawer';
import { connect } from 'react-redux';
import { toggleControl } from '../../../../store/control/duck';
import { injectIntl } from 'react-intl';
import { Grid, Radio, RadioGroup, FormControlLabel, Typography } from '@mui/material';
import FollowUpButton from '../FollowUpButton/FollowUpButton';
import ActionButton from '../ActionButton/ActionButton';
import { decreaseGPTQuota, handleError, resetGPTQuota, validateGPTQuota } from '../../../../utils/openai';
import { withRouter } from 'react-router';
import LoadingBackdrop from '../../../LoadingBackdrop/LoadingBackdrop';
import ContentBox from '../ContentBox/ContentBox';
import { openaiGenerateWithTextStreaming } from '../../../../store/openai/api';
import { useForceUpdate } from '../../../../utils/hooks';
import { trackDirect } from '../../../../store/analytics/api';
import { tracking } from '../../../../utils/tracking';
import FormLabelWithIcon from '../Inputs/FormLabelWithIcon';
import { LanguagePicker } from '../Inputs/Inputs';
import { LABEL_GAP } from '../Inputs/inputStyles';

const ChatGPTPersonalMessageDrawer = props => {
  const { intl, history, chatGPTPersonalMessageDrawer, toggleControl } = props;

  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  const [insuranceType, setType] = useState('');
  const [language, setLanguage] = useState(
    history.location.state?.language ? history.location.state.language : 'Traditional Chinese'
  );
  const forceUpdate = useForceUpdate();

  const { client, closed } = chatGPTPersonalMessageDrawer || {};

  const isOpen = !!chatGPTPersonalMessageDrawer && !closed;

  const onClose = () => {
    toggleControl('chatGPTPersonalMessageDrawer', { ...chatGPTPersonalMessageDrawer, closed: true });
  };

  const onExited = () => {
    toggleControl('chatGPTPersonalMessageDrawer', false);
    setText('');
  };

  const onFeedback = () => {
    toggleControl('chatGPTFeedbackDialog', {
      client: client,
      extraContent: text,
      onSubmit: () => {
        resetGPTQuota();
        forceUpdate();
      }
    });
  };

  const send = async () => {
    if (!insuranceType) {
      return;
    }

    try {
      validateGPTQuota();
      setLoading(true);
      trackDirect('useAiFeature');
      trackDirect('generatePersonalizedMessage');
      tracking('AI: Generate Personalized Message');

      await openaiGenerateWithTextStreaming(
        'generate_personalized_message',
        { type: insuranceType, language },
        [],
        setText,
        null,
        setLoading
      );

      decreaseGPTQuota();
    } catch (error) {
      handleError(error);
      setLoading(false);
    }
  };

  const clear = () => {
    setText('');
  };

  return (
    <Fragment>
      <LoadingBackdrop open={loading} />
      <ChatGPTPresetDrawer
        open={isOpen}
        onClose={onClose}
        onExited={onExited}
        onFeedback={onFeedback}
        title={intl.formatMessage({ id: 'chat-gpt-options-personal-msg' })}
        subtitle={intl.formatMessage({ id: 'gpt-subtitle-personal-msg' })}
        showQuota
      >
        <Grid container spacing={2} wrap="nowrap" style={{ minHeight: '100%' }} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography style={{ whiteSpace: 'pre-wrap' }}>
              {intl.formatMessage({ id: 'chat-gpt-personal-msg-drawer-content' })}
            </Typography>
          </Grid>
          <Grid>
            <FormLabelWithIcon
              iconName="Target"
              labelText={intl.formatMessage({ id: 'Insurance Type' })}
              type="purple"
            />
            <RadioGroup
              row
              aria-label="type"
              name="type"
              value={insuranceType}
              onChange={event => setType(event.target.value)}
            >
              <FormControlLabel
                value="Life"
                control={<Radio color="primary" style={{ paddingTop: '2px', paddingBottom: '2px' }} />}
                label={intl.formatMessage({ id: 'life' })}
              />
              <FormControlLabel
                value="C.I."
                control={<Radio color="primary" style={{ paddingTop: '2px', paddingBottom: '2px' }} />}
                label={intl.formatMessage({ id: 'C.I.' })}
              />
              <FormControlLabel
                value="Medical"
                control={<Radio color="primary" style={{ paddingTop: '2px', paddingBottom: '2px' }} />}
                label={intl.formatMessage({ id: 'Medical' })}
              />
              <FormControlLabel
                value="Saving"
                control={<Radio color="primary" style={{ paddingTop: '2px', paddingBottom: '2px' }} />}
                label={intl.formatMessage({ id: 'Saving' })}
              />
            </RadioGroup>
          </Grid>
          {/* Output language and the CTA share one Grid item, so the container's
              16px row gap falls above the pair instead of between them. The
              language is not another question about the client like 保險類型
              above — it is a setting ON the generation, so it reads centred and
              close over the button it applies to. `fullWidth` is what lets it
              centre at all: FormControl is inline-flex by default, and the
              picker's own `justifyContent: center` has nothing to centre
              within a box already shrunk to its content. */}
          <Grid style={{ width: '100%' }}>
            <LanguagePicker
              intl={intl}
              options={{ language }}
              onValueChange={(field, value) => setLanguage(value)}
              formControlProps={{ fullWidth: true, style: { marginBottom: LABEL_GAP } }}
            />
            <ActionButton onClick={send}>{intl.formatMessage({ id: 'gpt-message-generate-cta' })}</ActionButton>
          </Grid>
          <Grid>
            <ContentBox text={text} />
          </Grid>
          <Grid>
            <Grid container spacing={1} sx={{ flexDirection: 'column' }}>
              {text && (
                <Grid>
                  <Grid container spacing={1}>
                    <Grid>
                      <FollowUpButton onClick={clear}>
                        {intl.formatMessage({ id: 'chat-gpt-follow-up-clear' })}
                      </FollowUpButton>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </ChatGPTPresetDrawer>
    </Fragment>
  );
};

const container = connect(
  state => ({
    chatGPTPersonalMessageDrawer: state.control.chatGPTPersonalMessageDrawer
  }),
  {
    toggleControl
  }
)(ChatGPTPersonalMessageDrawer);

export default injectIntl(withRouter(container));
